package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.EvidenceContainerStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.components.widgets.YouTubeVideoCard
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Scale Up Reveal
@Composable
private fun Modifier.scaleUpReveal(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun CliniciansEvidenceSection(data: CliniciansEvidenceSectionData = defaultCliniciansEvidenceData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-evidence-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.videoUrl.isNotBlank()) {
        Column(
            modifier = EvidenceContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).scaleUpReveal(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )
                    if (data.sectionSubtitle.isNotBlank()) Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 40.px).scaleUpReveal(isVisible, 150).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                            }
                        })

                    if (data.videoUrl.isNotBlank()) {
                        Box(
                            modifier = Modifier.fillMaxWidth()
                                .maxWidth(if (data.videoFormat == VideoFormat.CINEMATIC_ULTRAWIDE) 1440.px else 1000.px)
                                .scaleUpReveal(isVisible, 300), contentAlignment = Alignment.Center
                        ) {
                            YouTubeVideoCard(
                                embedUrl = data.videoUrl,
                                thumbnailUrl = data.videoThumbnailUrl,
                                ariaLabel = data.videoAriaLabel,
                                labelPillText = data.videoLabelPillText,
                                videoFormat = data.videoFormat,
                                modifier = Modifier.fillMaxWidth().boxShadow(
                                    0.px,
                                    20.px,
                                    40.px,
                                    color = palette.custom.darkText.toRgb().copyf(alpha = 0.15f)
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS VIDEO UPLOAD SPECS FOR EVIDENCE SECTION:
 * @field videoUrl: Must be a standard embed URL (e.g., https://www.youtube.com/embed/<ID>).
 * The custom YouTubeVideoCard handles fetching the high-res thumbnail automatically.
 */
data class CliniciansEvidenceSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Driving Neuroplasticity Through Precision Mechatronics",
    val sectionSubtitle: String = "Watch the MyHand<sup>®</sup> System translates the fundamental principles of “Neuroplasticity” and decades of neuroscience research into a clinically validated, high-repetition, precision mechatronic solution that fundamentally changes hand rehabilitation post stroke.",
    val videoUrl: String = Res.Strings.Links.MYHAND_SCIENCE,
    val videoThumbnailUrl: String? = null,
    val videoLabelPillText: String? = "Watch · The Science of MyHand<sup>®</sup> System",
    val videoAriaLabel: String = "Play video explaining the science and neuroplasticity behind the MyHand® System",
    val videoFormat: VideoFormat = VideoFormat.STANDARD_16_9
)

val defaultCliniciansEvidenceData = CliniciansEvidenceSectionData()